// Cityscape.tsx — the 'cityscape' decorative media variant.
//
// Extracted VERBATIM from the old PostMedia.tsx. This is a CSS ornament on seeded demo
// posts, not a photograph: it is aria-hidden, never interactive, and never opens the viewer.

import styled, { keyframes } from 'styled-components';

const SKYLINE_HEIGHTS = [34, 52, 28, 68, 40, 60, 30, 58, 38, 72, 44, 50, 32, 64, 36];

const scanline = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 0 60px; }
`;

const CityscapeWrapper = styled.div<{ $fill?: boolean }>`
  position: relative;
  width: 100%;
  height: ${({ $fill }) => ($fill ? '100%' : '140px')};
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.bgRoot};

  /* Perspective grid */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(${({ theme }) => theme.color.brandFrom}22 1px, transparent 1px),
      linear-gradient(90deg, ${({ theme }) => theme.color.brandFrom}22 1px, transparent 1px);
    background-size: 40px 40px;
    animation: ${scanline} 3s linear infinite;
    opacity: 0.5;
  }

  /* Horizon glow */
  &::after {
    content: '';
    position: absolute;
    bottom: 28%;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.color.brandFrom},
      ${({ theme }) => theme.color.brandTo},
      ${({ theme }) => theme.color.brandFrom},
      transparent
    );
    box-shadow: 0 0 12px 3px ${({ theme }) => theme.color.brandFrom}88;
    opacity: 0.8;
  }

  /* The scanline is decorative motion. Honour the OS/user reduced-motion preference. */
  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const Skyline = styled.div`
  position: absolute;
  bottom: 28%;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  height: 72%;
  padding: 0 8px;
`;

const Building = styled.div<{ $h: number }>`
  width: clamp(10px, 4%, 18px);
  height: ${({ $h }) => $h}%;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.color.brandFrom} 0%,
    ${({ theme }) => theme.color.brandTo} 100%
  );
  border-radius: 2px 2px 0 0;
  opacity: 0.85;
  box-shadow:
    0 0 6px ${({ theme }) => theme.color.brandFrom}99,
    0 0 2px ${({ theme }) => theme.color.brandTo}66;

  /* Window slots */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      180deg,
      transparent,
      transparent 4px,
      ${({ theme }) => theme.color.accentCyan}44 4px,
      ${({ theme }) => theme.color.accentCyan}44 5px
    );
    border-radius: inherit;
  }
`;

export function Cityscape({ fill = false }: { fill?: boolean }) {
  return (
    <CityscapeWrapper $fill={fill} data-testid="post-media-cityscape" aria-hidden="true">
      <Skyline>
        {SKYLINE_HEIGHTS.map((h, i) => (
          <Building key={i} $h={h} />
        ))}
      </Skyline>
    </CityscapeWrapper>
  );
}
